// ============================================================================
// BonBoZ: CẤU HÌNH THEO TRANG — MỘT bộ nạp dùng chung cho cả app.
//
// Một endpoint trả cả ba thứ (màu, bộ thẻ, mẫu trả lời) cho MỌI inbox trong
// một lượt, nên chỉ cần một chỗ nạp và một bản đồ trong bộ nhớ. Trước đây là
// hai helper (`pageColor.js`, `cannedByPage.js`) mỗi cái tự gọi service CRM.
//
// Vì sao phải giữ `inFlight`: `InboxName.vue` render vài chục lần trong một
// danh sách hội thoại và mỗi lần đều gọi nạp — không dùng chung promise thì
// mỗi lần cuộn là vài chục request. Ràng buộc này vẫn còn nguyên giá trị.
//
// DEGRADE: `pageConfigs = null` nghĩa là CHƯA nạp / nạp hỏng (API lỗi, mất
// mạng), khác hẳn `{}` là "đã nạp, chưa trang nào cấu hình". Mọi selector đọc
// null đều trả về "không lọc gì": không viền màu, hiện tất cả thẻ, hiện tất cả
// mẫu. Cấu hình theo trang là tiện nghi, hỏng nó không được làm hỏng việc trực
// page.
//
// GET trả về MỌI inbox của account, trang chưa cấu hình có `labels: []` chứ
// KHÔNG vắng mặt. Vì vậy "chưa cấu hình" phải nhận ra bằng MẢNG RỖNG, không
// bằng `bag[id] === undefined` — nhánh undefined không bao giờ chạy nữa và
// lưới thẻ sẽ trống trơn thay vì hiện tất cả. Hệ quả: không cấu hình được
// trạng thái "trang này hiện 0 thẻ" — đúng như bản cũ, không phải hồi quy.
// ============================================================================
import { ref } from 'vue';
import PageConfigsAPI, { loiApi } from 'dashboard/api/pageConfigs';

/** { "<inbox_id>": { labels: [...], color: "#RRGGBB"|null, canned_response_ids: [...] } } */
export const pageConfigs = ref(null);

/**
 * Lý do lần nạp gần nhất hỏng; rỗng = ổn. Chỗ TIÊU THỤ (lưới tag, danh sách
 * hội thoại) im lặng degrade, còn màn CÀI ĐẶT phải nói rõ vì sao trống — nuốt
 * lỗi ở đó thì "chưa ai cấu hình" và "không có quyền" trông y hệt nhau.
 */
export const loiNap = ref('');

let inFlight = null;

/** `force`: nạp lại thật, dùng ở màn cài đặt để thấy thay đổi của người khác. */
export const loadPageConfigs = (force = false) => {
  if (inFlight && !force) return inFlight;
  inFlight = PageConfigsAPI.get()
    .then(({ data }) => {
      pageConfigs.value = data || {};
      loiNap.value = '';
      return pageConfigs.value;
    })
    .catch(e => {
      loiNap.value = loiApi(e);
      return pageConfigs.value;
    });
  return inFlight;
};

const cuaTrang = inboxId => pageConfigs.value?.[String(inboxId)];

/**
 * Gộp tại chỗ để giao diện đổi ngay khỏi tải lại. Gộp NÔNG đúng như server làm:
 * chỉ đè những khoá được truyền vào. PATCH trả về TOÀN BỘ cấu hình của trang
 * sau khi gộp, nên truyền thẳng `data` của nó vào đây là xong — không GET lại.
 */
export const setPageConfig = (inboxId, phan) => {
  const id = String(inboxId);
  pageConfigs.value = {
    ...(pageConfigs.value || {}),
    [id]: { ...(pageConfigs.value?.[id] || {}), ...phan },
  };
};

// --- MÀU NHẬN DIỆN TRANG ----------------------------------------------------
// 10+ page BonBoZ dùng chung bộ nhận diện, avatar na ná nhau nên không phân
// biệt được trang bằng mắt. Màu là kênh nhận diện duy nhất còn lại.
export const pageColorOf = inboxId => cuaTrang(inboxId)?.color || '';

// --- BỘ THẺ CỦA TRANG -------------------------------------------------------
// Label của Chatwoot thuộc CẢ TÀI KHOẢN; đây là bộ lọc "trang này dùng thẻ
// nào". Trang chưa cấu hình (mảng rỗng / chưa nạp) -> hiện tất cả thẻ.
export const pageLabelsOf = inboxId => cuaTrang(inboxId)?.labels || [];

export const locTheTheoTrang = (danhSach, inboxId) => {
  const rieng = pageLabelsOf(inboxId);
  if (!rieng.length) return danhSach;
  const cho = new Set(rieng);
  return danhSach.filter(l => cho.has(l.title));
};

// --- MẪU TRẢ LỜI THEO TRANG -------------------------------------------------
// Một kho `canned_responses` cấp tài khoản, cộng danh sách gán trang:
//   * mẫu KHÔNG được gán trang nào -> mẫu chung, mọi trang đều thấy;
//   * mẫu CÓ gán -> chỉ những trang đó thấy.
// Khoá inbox là chuỗi (đi qua JSON), id mẫu là số — so sánh phải ép kiểu.
export const idMauCuaTrang = inboxId =>
  new Set((cuaTrang(inboxId)?.canned_response_ids || []).map(Number));

/** Những mẫu đã được gán cho ÍT NHẤT một trang = mẫu "riêng". */
const idMauDaGan = () => {
  const rieng = new Set();
  Object.values(pageConfigs.value || {}).forEach(c =>
    (c?.canned_response_ids || []).forEach(id => rieng.add(Number(id)))
  );
  return rieng;
};

export const locMauTheoTrang = (danhSach, inboxId) => {
  if (!pageConfigs.value) return danhSach; // chưa nạp/hỏng -> hiện tất cả
  const rieng = idMauDaGan();
  const cuaMinh = idMauCuaTrang(inboxId);
  return danhSach.filter(
    m => cuaMinh.has(Number(m.id)) || !rieng.has(Number(m.id))
  );
};
